<template>
	<div class="settings-container display-flex flex-column width-100 height-100 pb-9">
		<div class="header-container pl-3 display-flex flex-column flex-align-start flex-justify-center">
			<div class="title-text">Feature appearance</div>
			<div class="title-description-text">Affects all interfaces in the feature</div>
		</div>
		<div v-if="isTool" class="section-container width-100 display-flex flex-column">
			<SettingSwitch
				title="Custom page title"
				:textFieldValue.sync="pageTitleValue"
				:switchValue.sync="isPageTitleEnabled"
				description="Displays as the browser tab name"
			/>
		</div>
		<div v-if="isTool" class="section-container width-100 display-flex flex-column">
			<SettingSwitch description="The icon in the browser tab" title="Favicon" :switchValue.sync="isFaviconEnabled" />
			<FlowrunFaviconUpload v-if="isFaviconEnabled" />
		</div>
		<div v-if="isTool" class="section-container width-100 display-flex flex-column">
			<SettingSwitch
				title="Header icon"
				:switchValue.sync="isLogoEnabled"
				description="Usually a logo, Square image, recommended size: 50X50"
			/>
			<WidgetLogoUpload v-if="isLogoEnabled" />
		</div>
		<v-expansion-panels flat accordion>
			<ExpansionPanel v-if="isTool" class="pl-2 pr-6" title="Background">
				<FeatureBackgroundImageMenu />
			</ExpansionPanel>
			<ExpansionPanel class="pl-2 pr-6" title="Progress bar">
				<FeatureProgressBarSettings />
			</ExpansionPanel>
			<ExpansionPanel class="pl-2 pr-6" title="Errors">
				<SettingSwitch
					:switchValue.sync="hideErrorsFromOrganization"
					title="Hide error details from users inside the workspace"
					description="hide error details from teammates whom are not workflow owners"
				/>
				<SettingSwitch
					class="mt-2"
					description="recommended"
					:switchValue.sync="hideErrorsFromNonOrganization"
					title="Hide error details from non-workspace users"
				/>
			</ExpansionPanel>
		</v-expansion-panels>
	</div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { updateWorkflowSetting } from '@/store/modules/selectedWorkflow/actions.js';
import { getWorkflowAppearanceSettings } from '@/store/modules/selectedWorkflow/getters.js';

import { isOfTypeTool } from '@/store/modules/selectedWorkflow/getters';
import SettingSwitch from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/SettingSwitch';
import WidgetLogoUpload from '@/components/BlockEditorWidget/FieldSettings/PageOptions/WidgetLogoUpload';
import ExpansionPanel from '@/components/BlockEditorWidget/FieldSettings/WidgetSettings/ExpansionPanel';
import FlowrunFaviconUpload from '@/components/BlockEditorWidget/FieldSettings/PageOptions/FlowrunFaviconUpload';
import FeatureBackgroundImageMenu from '@/components/BlockEditorWidget/FieldSettings/PageOptions/FeatureBackgroundImageMenu';
import FeatureProgressBarSettings from '@/components/BlockEditorWidget/FieldSettings/PageOptions/FeatureProgressBarSettings';

function getWorkflowSettingsComputed(key, defaultValue) {
	return {
		get() {
			return this.workflowAppearanceSettings[key] ?? defaultValue;
		},
		set(value) {
			this.updateWorkflowVersionSettings({ key, value });
		},
	};
}

export default {
	name: 'FeatureAppearanceSideBar',
	components: {
		SettingSwitch,
		ExpansionPanel,
		WidgetLogoUpload,
		FlowrunFaviconUpload,
		FeatureBackgroundImageMenu,
		FeatureProgressBarSettings,
	},
	computed: {
		...mapGetters([isOfTypeTool.fullPath, getWorkflowAppearanceSettings.fullPath]),
		workflowAppearanceSettings() {
			return this[getWorkflowAppearanceSettings.fullPath];
		},
		isLogoEnabled: getWorkflowSettingsComputed('logo_enabled'),
		isFaviconEnabled: getWorkflowSettingsComputed('favicon_enabled'),
		isPageTitleEnabled: getWorkflowSettingsComputed('page_title_enabled'),
		pageTitleValue: getWorkflowSettingsComputed('page_title', ''),
		hideErrorsFromOrganization: getWorkflowSettingsComputed('should_hide_errors_from_organization_users'),
		hideErrorsFromNonOrganization: getWorkflowSettingsComputed('should_hide_errors_from_non_organization_users', true),
		isTool() {
			return this[isOfTypeTool.fullPath];
		},
	},
	methods: {
		...mapActions([updateWorkflowSetting.fullPath]),
		updateWorkflowVersionSettings(payload) {
			this[updateWorkflowSetting.fullPath](payload);
		},
	},
};
</script>

<style scoped lang="scss">
.settings-container {
	overflow-y: auto;
	overflow-x: hidden;

	.section-container {
		padding: 14px 10px 14px 8px;
		border-bottom: 1px solid var(--border-color);
	}
	.header-container {
		min-height: 63px;
		border-bottom: 1px solid var(--border-color);

		.title-text {
			font-size: 16px;
			font-weight: bold;
			color: var(--primary-font-color);
		}
		.title-description-text {
			font-size: 12px;
			color: var(--secondary-font-color);
		}
	}
}
</style>
